import { format } from "date-fns";
import { getLocale } from "./languages";

/**
 * Format a datetime to ISO 8601 compete date, plus hours, minutes, and seconds.
 * @param {String} datetimeString
 * @returns
 */
export const formatActivateStartDatetime = (datetimeString) => {
  const date = new Date(datetimeString);
  const month = `0${date.getMonth() + 1}`.slice(-2);
  const day = `0${date.getDate()}`.slice(-2);
  const hours = `0${date.getHours()}`.slice(-2);
  const minutes = `0${date.getMinutes()}`.slice(-2);
  const seconds = `0${date.getSeconds()}`.slice(-2);

  return `${[date.getFullYear(), month, day].join(
    "-",
  )}T${hours}:${minutes}:${seconds}`;
};

export const formatDisplayDatetime = (dateString, languageCode = "NL") => {
  if (!dateString) {
    return "";
  }

  return format(new Date(dateString), "P pp", {
    locale: getLocale(languageCode),
  });
};

export const formatDisplayDatetimeMinutes = (dateString, languageCode) => {
  return format(new Date(dateString), "P p", {
    locale: getLocale(languageCode),
  });
};

export const formatDatetime = (dateString) => {
  const date = new Date(dateString);

  return date.toDateString();
};

export const formatDatetime2 = (languageCode, dateString) => {
  const date = new Date(dateString);
  const options = { year: "numeric", month: "long", day: "numeric" };

  switch (languageCode) {
    case "EN":
      return date.toLocaleString("en-US", options);
    case "DE":
      return date.toLocaleString("de-DE", options);
    case "FR":
      return date.toLocaleString("fr-FR", options);
    default:
      return date.toLocaleString("nl-NL", options);
  }
};

export const formatDatetime3 = (languageCode, date) => {
  const options = { year: "numeric", month: "long", day: "numeric" };

  switch (languageCode) {
    case "EN":
      return date.toLocaleString("en-US", options);
    case "DE":
      return date.toLocaleString("de-DE", options);
    case "FR":
      return date.toLocaleString("fr-FR", options);
    default:
      return date.toLocaleString("nl-NL", options);
  }
};

export const formatDatetoTime = (languageCode, date) => {
  const options = { hour: "numeric", minute: "numeric", second: "numeric" };
  switch (languageCode) {
    case "EN":
      return date.toLocaleString("en-US", options);
    case "DE":
      return date.toLocaleString("de-DE", options);
    case "FR":
      return date.toLocaleString("fr-FR", options);
    default:
      return date.toLocaleString("nl-NL", options);
  }
};

/**
 * Format a date to dd/MM/yy string.
 * @param {Date} datet
 * @returns A string in the form dd/MM/yy
 */
export const formatDatetoString = (date) => {
  return format(date, "dd/MM/yy");
};

export const formatDate = (date) => {
  return date.split("T")[0].split("-").reverse().join("-");
};

export const formatFlatTransactionAmount = (
  transactionAmount,
  currencyCode,
) => {
  switch (currencyCode) {
    case "EUR":
      return transactionAmount / 100.0;
    default:
      return transactionAmount;
  }
};

/**
 * Format transaction dates and amount to human readable format.
 * @param {Array} transactions An array of transaction objects.
 * @returns An array of formatted transactions.
 */
export const formatTransactions = (transactions) => {
  const formattedTransactions = [];

  transactions.forEach((transaction) => {
    formattedTransactions.push({
      amount: formatFlatTransactionAmount(
        transaction.amount,
        transaction.currency,
      ),
      cardNumber: transaction.tag
        ? `${transaction.cardNumber}-${transaction.tag}`
        : transaction.cardNumber,
      creationDate: formatDisplayDatetime(transaction.creationDate),
      currency: transaction.currency,
      operationName: transaction.operationName,
      productName: transaction.productName,
      tag: transaction.tag,
      retailer: transaction.retailerName,
    });
  });

  return formattedTransactions;
};

/**
 * Format a currency code into a symbol representation.
 * @param {String} currencyCode
 * @returns The currency code in symbol representation.
 */
export const formatCurrencyCode = (currencyCode) => {
  switch (currencyCode) {
    case "EUR":
      return "€";
    case "PGK":
    case "PNT":
      return "Point(s)";
    case "UNI":
      return "Unit(s)";
    default:
      return "";
  }
};

/**
 * Format an amount, and currency code into a text representation.
 * @param {Number} amount
 * @param {String} currencyCode
 * @returns The money in text representation.
 */
export const formatAmount = (amount, currencyCode) => {
  switch (currencyCode) {
    case "EUR":
      return `${formatCurrencyCode(currencyCode)} ${Number(
        amount / 100.0,
      ).toLocaleString("nl-NL", { minimumFractionDigits: 2 })}`;
    default:
      return `${amount} ${formatCurrencyCode(currencyCode)}`;
  }
};

export const formatLanguageCodeToLocales = (languageCode) => {
  switch (languageCode) {
    case "EN":
      return "en-US";
    case "DE":
      return "de-DE";
    case "FR":
      return "fr-FR";
    case "ES":
      return "es-ES";
    default:
      return "nl-NL";
  }
};

/**
 * Format an amount, currency code, language code into a text representation.
 * @param {Number} amount
 * @param {String} currencyCode
 * @param {String} languageCode
 * @returns The money in text representation.
 */
export const formatAmountWithLanguageCode = (
  amount,
  currencyCode,
  languageCode,
) => {
  switch (currencyCode) {
    case "EUR":
      return `${formatCurrencyCode(currencyCode)} ${Number(
        amount / 100,
      ).toLocaleString(formatLanguageCodeToLocales(languageCode), {
        minimumFractionDigits: 2,
      })}`;
    case "UNI":
      return `${amount.toLocaleString(
        formatLanguageCodeToLocales(languageCode),
        { minimumFractionDigits: 2 },
      )} ${formatCurrencyCode(currencyCode)}`;
    case "PGK":
      return `${amount.toLocaleString(
        formatLanguageCodeToLocales(languageCode),
        { minimumFractionDigits: 2 },
      )} ${formatCurrencyCode(currencyCode)}`;
    case "PNT":
      return `${amount.toLocaleString(
        formatLanguageCodeToLocales(languageCode),
        { minimumFractionDigits: 2 },
      )} ${formatCurrencyCode(currencyCode)}`;
    default:
      return `${amount} ${formatCurrencyCode(currencyCode)}`;
  }
};

/**
 * Format a transaction into a text representation.
 * @param {Number} amount
 * @param {String} currencyCode
 * @param {String} command
 * @returns The transaction in text representation.
 */
export const formatTransactionAmount = (amount, currencyCode, command) => {
  const money = formatAmount(amount, currencyCode);

  switch (command) {
    case "retire":
    case "withdraw":
      return `- ${money}`;
    case "subtract":
      return `- ${money}`;
    case "activate":
    case "refill":
      return `+ ${money}`;
    case "add":
      return `+ ${money}`;
    default:
      return money;
  }
};

/**
 * Format the card number to be a certain length.
 * @param {String} cardNumber
 * @returns The formatted card number.
 */
export const formatCardNumber = (cardNumber) => {
  let formattedCardNumber = cardNumber;

  if (cardNumber === undefined || cardNumber.length === 0) return "";

  // Take the last 19 digits if the card number is longer than 23.
  if (formattedCardNumber.length > 23) {
    formattedCardNumber = formattedCardNumber.slice(-23);
  }

  return formattedCardNumber.match(/.{1,4}/g).join(" ");
};

/**
 * Format the card number and blind last 3 numbers to be a certain length.
 * @param {String} cardNumber
 * @returns The formatted card number with blinded last 3 numbers.
 */
export const formatCardNumberAnonymous = (cardNumber) => {
  let anonCardnumber;
  anonCardnumber = cardNumber.slice(0, -4);
  anonCardnumber = anonCardnumber.concat("*", "*", "*", "*");

  return formatCardNumber(anonCardnumber);
};

/**
 * Converts a CSS string to a list of classes and ids.
 * @param {string} cssString The CSS string to process.
 * @returns {[string[], string[]]} An array with two elements: a list of class names and a list of id names.
 */
export const formatCSStoSelectors = (cssString) => {
  const classRegex = /\.([^{\n]+)/g;
  const idRegex = /#([^{\n]+)/g;

  const classes = [];
  const ids = [];

  let match;

  do {
    match = classRegex.exec(cssString);
    if (match) {
      classes.push(match[1]);
    }
  } while (match);

  do {
    match = idRegex.exec(cssString);
    if (match) {
      ids.push(match[1]);
    }
  } while (match);

  return [classes, ids];
};

/**
 * Converts lists with classes and ids to a CSS string.
 * @param {string[]} classes An array with classnames.
 * @param {string[]} ids An array with id names.
 * @returns {string} A string with CSS code
 *
 */
export const formatSelectorsCSS = (classes, ids) => {
  let CSSString = "";

  classes.forEach((element) => {
    CSSString = CSSString.concat(".", element, " {", "\n\n", "}", "\n");
  });

  ids.forEach((element) => {
    CSSString = CSSString.concat("#", element, " {", "\n\n", "}", "\n");
  });

  return CSSString;
};

/**
 * Converts a HTML string to lists with classes and ids.
 * @param {string} HTMLString A string with HTML
 * @returns {Object} An object with 2 keys: classes and ids, containing corresponding lists
 */
export const formatHTMLtoSelectors = (HTMLString) => {
  const classSet = new Set();
  const idSet = new Set();

  const classRegex = /class=["']([^"']+)["']/gi;
  const idRegex = /id=["']([^"']+)["']/gi;

  let match;

  do {
    match = classRegex.exec(HTMLString);
    if (match) {
      match[1].split(" ").forEach((className) => classSet.add(className));
    }
  } while (match);

  do {
    match = idRegex.exec(HTMLString);
    if (match) {
      idSet.add(match[1]);
    }
  } while (match);

  const classes = Array.from(classSet);
  const ids = Array.from(idSet);

  return { classes, ids };
};
